import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useProject } from '../../../context/ProjectContext';
import studyProtocolService from '../../../services/studyProtocol.service';
import { generateContent } from '../../../services/AIService';
import documentEditorService from '../../../services/documentEditor.service';
import feasibilityService from '../../../services/feasibility.service';
import { useLocation, useParams } from 'react-router-dom';

const buildPrompt = () => `
You are a Clinical Trial Site Feasibility Expert with 10+ years of experience in pharmaceutical clinical trial operations. Your role is to analyze clinical trial protocols and develop comprehensive site feasibility questionnaires that assess investigative sites' ability to recruit, screen, and manage patients according to specific inclusion/exclusion criteria.

TASK: Generate a Site Feasibility Questionnaire as a JSON array of question objects based on the inclusion and exclusion criteria provided in the context.

OUTPUT FORMAT: Return ONLY a valid JSON array. Each question object must have the following structure:
{
  "question_text": "The full question text",
  "section": "Section name (e.g., 'Patient Population & Database', 'Specific Inclusion Criteria', 'Specific Exclusion Criteria', 'Site Capabilities & Procedures')",
  "expected_response_type": "Response type (e.g., 'Number', 'Yes/No', 'Text', 'Range')",
  "draft_answer": "A sample or suggested draft answer to guide sites (optional but recommended)",
  "rationale": "Brief justification or notes for why this question is important (optional)"
}

INSTRUCTIONS:

1. Group questions into logical sections:
   - "Patient Population & Database"
   - "Specific Inclusion Criteria"
   - "Specific Exclusion Criteria"
   - "Site Capabilities & Procedures"
   - "Recruitment & Enrollment Projections"
   - "Challenges & Mitigations"

2. Be specific and reference criteria directly:
   - Instead of generic questions, reference specific inclusion/exclusion criteria by number and details
   - Example: "Based on Inclusion Criterion #3 (age ≥18 years), how many patients in your database currently meet this requirement?"

3. For criteria requiring specific tests, scans, or assessments:
   - Ask if the site can perform the procedure on-site (Yes/No)
   - Ask about standard turnaround time for results (Number, days)
   - Ask if they utilize a central vendor (Yes/No)
   - Ask about availability of trained staff (Yes/No)

4. Include questions about:
   - Patient population estimates based on specific criteria
   - Site capabilities for required procedures/tests
   - Top 3 anticipated challenges related to specific inclusion/exclusion criteria
   - Realistic monthly screening and enrollment projections
   - Competing trials that might affect enrollment
   - Experience with vulnerable populations and informed consent strategies (if applicable)

5. Response types should be one of: "Number", "Yes/No", "Text", "Range", "Percentage", "Days", "Patients/Month"

6. Provide draft answers where helpful to guide sites on expected response format and detail level.

CONSTRAINTS:

- Base questions SOLELY on the provided inclusion/exclusion criteria
- If a criterion is ambiguous, ask sites to describe their standard practice for interpretation
- Request estimated ranges with rationale when exact numbers cannot be provided
- Ensure questions are practical and answerable by site staff
- Include a mix of quantitative (numbers) and qualitative (text) questions

IMPORTANT: Return ONLY the JSON array. Do NOT include markdown code blocks, explanations, or any other text. The response must be valid JSON that can be parsed directly.

Example JSON structure:
[
  {
    "question_text": "How many patients with the target indication are currently in your database?",
    "section": "Patient Population & Database",
    "expected_response_type": "Number",
    "draft_answer": "Estimated number of patients",
    "rationale": "Assesses baseline patient pool availability"
  },
  {
    "question_text": "Based on Inclusion Criterion #1 (age ≥18 years), how many patients in your database currently meet this requirement?",
    "section": "Specific Inclusion Criteria",
    "expected_response_type": "Number",
    "draft_answer": "Estimated number meeting age requirement",
    "rationale": "Evaluates patient pool after applying age inclusion criterion"
  },
  {
    "question_text": "Can your site perform echocardiograms on-site?",
    "section": "Site Capabilities & Procedures",
    "expected_response_type": "Yes/No",
    "draft_answer": "Yes or No",
    "rationale": "Assesses on-site capability for required cardiac assessment"
  }
]

The inclusion and exclusion criteria will be provided in the context section. Generate the questionnaire based on those criteria.
`;

const stripMarkup = (value = '') => {
  if (typeof value !== 'string') return value;
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
};

const normaliseDraftAnswer = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === 'string' ? stripMarkup(item) : JSON.stringify(item))).join('\n');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return stripMarkup(String(value));
};

const flattenQuestions = (input, sectionHint = '') => {
  const results = [];

  const visit = (node, currentSection) => {
    if (!node && node !== 0) return;

    if (Array.isArray(node)) {
      node.forEach((item) => visit(item, currentSection));
      return;
    }

    if (typeof node === 'object') {
      // If this object itself represents a question
      const questionText = node.question_text || node.questionText || node.question || '';
      const draftAnswerRaw =
        node.draft_answer || node.draftAnswer || node.answer || node.draft_response || node.response || '';

      const sectionName =
        node.section ||
        node.section_name ||
        node.sectionTitle ||
        node.section_title ||
        currentSection ||
        '';

      const expectedType = node.expected_response_type || node.response_type || node.expectedType || '';

      const rationale = node.rationale || node.justification || node.notes || '';
      const confidence = node.confidence || node.confidence_rating || node.confidence_score || null;

      const questionIsValid = typeof questionText === 'string' && questionText.trim().length > 0;
      const hasDraftAnswer =
        typeof draftAnswerRaw === 'string' ||
        typeof draftAnswerRaw === 'number' ||
        typeof draftAnswerRaw === 'boolean' ||
        Array.isArray(draftAnswerRaw) ||
        typeof draftAnswerRaw === 'object';

      if (questionIsValid) {
        const cleanedQuestion = stripMarkup(questionText);
        const cleanedAnswer = hasDraftAnswer ? normaliseDraftAnswer(draftAnswerRaw) : '';

        const cleaned = {
          question_text: cleanedQuestion,
          draft_answer: cleanedAnswer,
        };

        if (sectionName) cleaned.section = stripMarkup(sectionName);
        if (expectedType) cleaned.expected_response_type = stripMarkup(expectedType);
        if (rationale) cleaned.rationale = typeof rationale === 'string' ? stripMarkup(rationale) : rationale;
        if (confidence !== null && confidence !== undefined) cleaned.confidence = confidence;

        // Preserve any additional metadata
        Object.entries(node).forEach(([key, value]) => {
          if (cleaned[key] !== undefined || key === 'question_text' || key === 'questionText' || key === 'question') {
            return;
          }
          cleaned[key] =
            typeof value === 'string'
              ? stripMarkup(value)
              : Array.isArray(value) || typeof value === 'object'
              ? value
              : value;
        });

        results.push(cleaned);
        return;
      }

      // Otherwise treat keys as possibly section -> array of questions
      Object.entries(node).forEach(([key, value]) => {
        const nextSection = typeof key === 'string' && key.toLowerCase().includes('section') ? key : currentSection;
        visit(value, nextSection || key);
      });
      return;
    }
  };

  visit(input, sectionHint);
  return results;
};

const FeasibilityQuestionnaire = () => {
  const { currentProject, loadProject } = useProject();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId') || queryParams.get('project');
  const { id: projectIdFromParams } = useParams();

  const [questions, setQuestions] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  const effectiveProjectId = useMemo(() => {
    return currentProject?._id || projectIdFromUrl || projectIdFromParams || '';
  }, [currentProject, projectIdFromParams, projectIdFromUrl]);

  const protocolNumber = useMemo(() => {
    if (currentProject) {
      return (
        currentProject.protocolNumber ||
        currentProject?.protocol?.number ||
        currentProject?.protocolId ||
        currentProject.protocol?.protocolNumber ||
        ''
      );
    }
    return '';
  }, [currentProject]);

  useEffect(() => {
    if (!currentProject && projectIdFromUrl) {
      loadProject(projectIdFromUrl);
    }
  }, [currentProject, projectIdFromUrl, loadProject]);

  useEffect(() => {
    let isMounted = true;

    const fetchExisting = async () => {
      if (!effectiveProjectId) return;
      setLoadingExisting(true);
      try {
        const payload = await feasibilityService.getQuestionnaire(effectiveProjectId);
        if (isMounted && payload) {
          setQuestions(Array.isArray(payload.questionnaire) ? payload.questionnaire : []);
        }
      } catch (fetchErr) {
        if (isMounted) {
          console.warn('Failed to fetch feasibility questionnaire:', fetchErr);
        }
      } finally {
        if (isMounted) {
          setLoadingExisting(false);
        }
      }
    };

    fetchExisting();

    return () => {
      isMounted = false;
    };
  }, [effectiveProjectId]);

  const extractProtocolCriteria = async () => {
    let protocolPayload = null;
    let inclusionText = '';
    let exclusionText = '';

    const collectFromSections = (sectionsInput = []) => {
      if (!Array.isArray(sectionsInput)) return;
      const inclusionSections = sectionsInput.filter((section) =>
        (section.title || '').toLowerCase().includes('inclusion')
      );
      const exclusionSections = sectionsInput.filter((section) =>
        (section.title || '').toLowerCase().includes('exclusion')
      );

      if (inclusionSections.length) {
        inclusionText = inclusionSections
          .map((section) => stripMarkup(section.output || section.context || ''))
          .filter(Boolean)
          .join('\n');
      }
      if (exclusionSections.length) {
        exclusionText = exclusionSections
          .map((section) => stripMarkup(section.output || section.context || ''))
          .filter(Boolean)
          .join('\n');
      }
    };

    if (protocolNumber) {
      try {
        protocolPayload = await studyProtocolService.getStudyProtocolByNumber(protocolNumber);
        if (protocolPayload?.sections) {
          collectFromSections(protocolPayload.sections);
        }
        if (!inclusionText && protocolPayload?.inclusionCriteria) {
          inclusionText = stripMarkup(protocolPayload.inclusionCriteria);
        }
        if (!exclusionText && protocolPayload?.exclusionCriteria) {
          exclusionText = stripMarkup(protocolPayload.exclusionCriteria);
        }
      } catch (err) {
        console.warn('Protocol lookup by number failed, will attempt project sections.', err);
      }
    }

    if ((!protocolPayload || !inclusionText || !exclusionText) && effectiveProjectId) {
      try {
        const protocolSections = await documentEditorService.getAllSections(effectiveProjectId);
        if (Array.isArray(protocolSections) && protocolSections.length) {
          protocolPayload = {
            projectId: effectiveProjectId,
            sections: protocolSections
              .filter(
                (section) =>
                  (section.documentType ? section.documentType === 'PROTOCOL' : true) &&
                  (section.output || section.context)
              )
              .map((section) => ({
                title: section.title,
                number: section.number || section.order || null,
                output: stripMarkup(section.output || ''),
                context: stripMarkup(section.context || ''),
              })),
          };
          collectFromSections(protocolPayload.sections);
        }
      } catch (sectionsError) {
        console.error('Fallback to document sections failed.', sectionsError);
      }
    }

    return {
      inclusionText,
      exclusionText,
      protocolPayload,
    };
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setInfoMessage(null);
    setSaveMessage(null);

    try {
      const { inclusionText, exclusionText, protocolPayload } = await extractProtocolCriteria();

      if (!protocolPayload) {
        throw new Error('Protocol data is unavailable for this project.');
      }

      const prompt = buildPrompt();

      // Format context with inclusion/exclusion criteria for the AI service
      // Include protocol summary with key sections relevant to feasibility (to avoid token limits)
      const protocolSummary = {
        projectId: protocolPayload?.projectId || effectiveProjectId,
        protocolNumber: protocolNumber || protocolPayload?.protocolNumber || protocolPayload?.number || 'Not specified',
      };

      // Extract specific protocol sections (3, 5, 6, 8, and 9) for feasibility assessment
      const sections = protocolPayload?.sections || [];
      const requiredSectionNumbers = [3, 5, 6, 8, 9];
      
      if (Array.isArray(sections) && sections.length > 0) {
        protocolSummary.keySections = sections
          .filter((section) => {
            // Check both number and order fields to match section numbers
            const sectionNumber = section.number || section.order;
            
            // Handle various formats: number, string number, or string with "Section X"
            let num = null;
            if (typeof sectionNumber === 'number') {
              num = sectionNumber;
            } else if (typeof sectionNumber === 'string') {
              // Try to extract number from strings like "3", "Section 3", "3.1", etc.
              const match = sectionNumber.match(/\b(\d+)\b/);
              if (match) {
                num = parseInt(match[1], 10);
              }
            }
            
            // Include sections 3, 5, 6, 8, and 9
            return num !== null && !isNaN(num) && requiredSectionNumbers.includes(num);
          })
          .map((section) => {
            const content = section.output || section.context || '';
            return {
              title: section.title,
              number: section.number || section.order || null,
              // Include full content for these specific sections (they're important for feasibility)
              content: content,
            };
          });
      }

      const context = `INCLUSION CRITERIA:\n${inclusionText || 'Not provided.'}\n\nEXCLUSION CRITERIA:\n${exclusionText || 'Not provided.'}\n\nPROTOCOL SUMMARY:\n${JSON.stringify(protocolSummary, null, 2)}`;

      const rawResponse = await generateContent(
        prompt,
        context,
        '',
        'feasibility',
        'general study-level Feasibility Q&A JSON'
      );

      const tryParse = (input) => {
        if (!input || !input.trim()) return null;

        const cleaned = input
          .replace(/^```json\s*/i, '')
          .replace(/^```/i, '')
          .replace(/```$/g, '')
          .trim();

        if (!cleaned) {
          throw new Error('AI response did not contain JSON output.');
        }

        return JSON.parse(cleaned);
      };

      let parsed;
      try {
        parsed = tryParse(rawResponse);
      } catch (jsonError) {
        console.warn('Failed to parse AI response as JSON, attempting to recover.', jsonError);

        const jsonMatch = rawResponse.match(/```json[\s\S]*?```/i) || rawResponse.match(/\{[\s\S]*\}\s*$/);
        if (!jsonMatch) {
          throw new Error('AI response did not contain a JSON block.');
        }

        parsed = tryParse(jsonMatch[0]);
      }

      const flattened = flattenQuestions(parsed);

      if (!flattened.length) {
        throw new Error('Generated questionnaire did not contain any questions.');
      }

      const normalized = flattened.map((question) => ({
        ...question,
        question_text: stripMarkup(question.question_text || ''),
        draft_answer: normaliseDraftAnswer(question.draft_answer || ''),
      }));

      if (!normalized.length) {
        throw new Error('Generated questionnaire contained no valid questions.');
      }

      setQuestions(normalized);
      setInfoMessage('Questionnaire with draft answers generated from the latest protocol criteria. Review and adjust as needed.');
    } catch (fetchError) {
      console.error('Error generating feasibility questionnaire:', fetchError);
      setError(fetchError?.message || 'Failed to generate questionnaire from protocol. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prev) =>
      prev.map((question, qIdx) => (qIdx === index ? { ...question, [field]: value } : question))
    );
  };

  const handleSave = async () => {
    if (!effectiveProjectId) {
      setError('Please select or load a project before saving the questionnaire.');
      return;
    }

    if (!questions.length) {
      setError('No questionnaire content to save. Generate or enter content before saving.');
      return;
    }

    setSaving(true);
    setError(null);
    setSaveMessage(null);

    try {
      await feasibilityService.saveQuestionnaire(effectiveProjectId, questions);
      setSaveMessage('Feasibility questionnaire saved successfully.');
    } catch (saveError) {
      console.error('Failed to save feasibility questionnaire:', saveError);
      setError(
        saveError?.response?.data?.message ||
          saveError?.message ||
          'Failed to save the feasibility questionnaire. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const renderQuestionnaire = () => {
    if (!questions.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No questionnaire loaded. Use "Auto-populate from Protocol" to generate questions.
        </Typography>
      );
    }

    return (
      <Box component="form" sx={{ mb: 3 }}>
        {questions.map((question, index) => (
          <Paper key={index} variant="outlined" sx={{ mb: 3, p: 3, borderRadius: 2, backgroundColor: '#fafbfc' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Question {index + 1}
            </Typography>
            <TextField
              fullWidth
              label="Question"
              value={question.question_text}
              onChange={(event) => handleQuestionChange(index, 'question_text', event.target.value)}
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Draft Answer"
              value={question.draft_answer}
              onChange={(event) => handleQuestionChange(index, 'draft_answer', event.target.value)}
              multiline
              minRows={3}
            />
          </Paper>
        ))}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Feasibility Questionnaire with Draft Answers</Typography>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={generating || loadingExisting}
          startIcon={generating ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ mr: 1 }}
        >
          {generating ? 'Generating…' : 'Auto-populate from Protocol'}
        </Button>
        <Button
          variant="outlined"
          onClick={handleSave}
          disabled={saving || generating || loadingExisting}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save Questionnaire'}
        </Button>
      </Box>

      {loadingExisting && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading existing questionnaire…
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {infoMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {infoMessage}
        </Alert>
      )}
      {saveMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      )}

      {renderQuestionnaire()}
    </Paper>
  );
};

export default FeasibilityQuestionnaire;

